import { useEffect, useMemo, useRef, useState } from 'react';
import {
  blockTimeLabel,
  canPrintInBrowser,
  documentKind,
  DOCUMENT_RULES,
  fileSizeLabel,
  isCustomPlan,
  isImageAttachment,
  SUB_FILE_RULES,
  SUB_TIER_LABELS,
  tierForDate,
  type ResolvedSubBlock,
  type ResolvedSubPlan,
  type SubAttachment,
  type SubBlock,
  type SubPlanTier,
  type SubTier,
  type PlanRow,
  type Worksheet,
} from '@td/shared';
import {
  useAddLink,
  useCreateSubPlan,
  useDeleteAttachment,
  useDeleteSubPlan,
  useCreateWorksheet,
  useDeleteWorksheet,
  useResetSubPlan,
  useUpdateWorksheet,
  useSubPlans,
  useUpdateSubPlan,
  useUploadAttachment,
} from '../hooks/useSubPlans';
import { Modal } from '../components/Modal';
import { DocumentThumbnail } from '../components/DocumentView';
import { documentUrlById, useDocuments, useUploadDocument } from '../hooks/useDocuments';
import { WorksheetSheet } from '../components/WorksheetSheet';
import { printDocuments } from '../lib/printFile';
import { WorksheetEditor } from '../components/WorksheetEditor';
import { Portal } from '../components/Portal';
import { useConfirm } from '../components/ConfirmDialog';
import { HeaderMenu } from '../components/HeaderMenu';
import { Doodle } from '../components/Doodle';
import { zonedNow } from '../lib/dates';
import { ApiRequestError } from '../lib/api';

/**
 * Sub plans.
 *
 * The thing to understand about this page: the times on it are not stored
 * anywhere here. Each block points at a row of the lesson plan, and the times
 * come off that row every time the page is drawn. Change Reading to nine
 * o'clock in Lesson Plans and this page has already changed.
 */

const VIEWS = ['plan', 'worksheets', 'attachments'] as const;
type View = (typeof VIEWS)[number];
const VIEW_LABELS: Record<View, string> = {
  plan: 'The day',
  worksheets: 'Worksheets',
  attachments: 'For the sub',
};

export function SubPlansPage() {
  const { data, isLoading } = useSubPlans();

  /*
   * One selection, not a tier and a day held separately.
   *
   * Her own plans have no tier and no day in any meaningful sense — "Flu
   * week" is not a time of year — so two independent pickers could not
   * express them. A single "which plan" is also what the page is really
   * asking, and it stays one control however many she writes.
   */
  const [picked, setPicked] = useState<{ tier: SubPlanTier; day: number }>(() => ({
    tier: tierForDate(zonedNow()),
    day: 1,
  }));
  const { tier, day } = picked;

  const [view, setView] = useState<View>('plan');
  const [printing, setPrinting] = useState<'packet' | string | null>(null);
  const [naming, setNaming] = useState(false);

  const createPlan = useCreateSubPlan();
  const deletePlan = useDeleteSubPlan();

  const plan = data?.plans.find((p) => p.tier === tier && p.day === day) ?? null;
  const mine = (data?.plans ?? []).filter((p) => isCustomPlan(p.tier));
  const worksheets = data?.worksheets ?? [];
  const schedule = data?.schedule ?? [];

  // Both live in the header's menu now, so both are held at page level.
  const reset = useResetSubPlan(tier, day);
  const confirm = useConfirm();

  /*
   * Printing goes through a portal rather than through the page.
   *
   * The page is a set of editing controls; the packet is a document. Trying to
   * make one print as the other means a print rule on every button on screen,
   * and one missed rule puts an "Edit" chip in the middle of a sub's plan.
   */
  useEffect(() => {
    if (!printing) return;

    // A frame for the portal to paint before the print dialog freezes it.
    const id = window.setTimeout(() => {
      window.print();
      setPrinting(null);
    }, 60);

    return () => window.clearTimeout(id);
  }, [printing]);

  if (isLoading) return <p className="text-sm text-stone">Loading…</p>;

  const packetSheets = plan
    ? worksheets.filter((w) => plan.blocks.some((b) => b.worksheetIds.includes(w.id)))
    : [];

  return (
    /*
     * The page steps out of the way only while a print is in flight. Left
     * permanently `print:hidden`, a teacher who pressed Ctrl+P out of habit
     * would get a blank sheet.
     */
    <div className={`mx-auto max-w-5xl ${printing ? 'print:hidden' : ''}`}>
      <header className="page-header flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl">Sub plans</h1>

        <HeaderMenu label="Sub plan settings">
          {(close) => (
            <div className="space-y-1.5">
              <button
                className="btn-soft w-full"
                onClick={() => {
                  setPrinting('packet');
                  close();
                }}
              >
                Print the packet
              </button>

              {/*
                Starting a day again is a twice-a-year thing that undoes an
                afternoon of writing, so it belongs behind the gear with the
                rest of the rarely-pressed controls rather than sitting at the
                bottom of the page she scrolls past every time.
              */}
              {/*
                Hers is deleted; a shipped one is only ever reset. A built-in
                plan she could delete outright is one she can wake up ill
                without, which is the single thing this page exists to prevent.
              */}
              {isCustomPlan(tier) ? (
                <button
                  className="btn-soft-danger w-full"
                  disabled={!plan}
                  onClick={async () => {
                    close();
                    const ok = await confirm({
                      title: `Delete "${plan?.title ?? 'this plan'}"?`,
                      body: 'This one is yours, so it goes for good. Your worksheets and documents are not touched.',
                      confirmLabel: 'Delete it',
                      danger: true,
                    });
                    if (ok) {
                      await deletePlan.mutateAsync({ tier, day });
                      setPicked({ tier: tierForDate(zonedNow()), day: 1 });
                    }
                  }}
                >
                  Delete this plan
                </button>
              ) : (
                <button
                  className="btn-soft-danger w-full"
                  disabled={!plan}
                  onClick={async () => {
                    close();
                    const ok = await confirm({
                      title: `Start ${SUB_TIER_LABELS[tier as SubTier].toLowerCase()}, day ${day}, again?`,
                      body: 'Every change you have made to this plan goes, and it returns to the one the app shipped with. Your worksheets and attachments are not touched.',
                      confirmLabel: 'Start again',
                      danger: true,
                    });
                    if (ok) reset.mutate();
                  }}
                >
                  Start this day again
                </button>
              )}
            </div>
          )}
        </HeaderMenu>
      </header>

      {/*
        Every plan is hers, so the picker is just a list of them.

        The app used to ship nine — three days each for the beginning, middle
        and end of the year — and this was a tier row, a day row and a view
        row before a word of the plan appeared. Megan did not want the shipped
        ones: a day written for somebody else's classroom takes longer to
        correct than to write.
      */}
      {mine.length > 0 && (
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <label className="min-w-[16rem] flex-1">
            <span className="label">Which plan</span>
            <select
              className="field"
              value={`${tier}:${day}`}
              onChange={(e) => {
                const [t, d] = e.target.value.split(':');
                setPicked({ tier: t as SubPlanTier, day: Number(d) });
              }}
            >
              {mine.map((p) => (
                <option key={`${p.tier}:${p.day}`} value={`${p.tier}:${p.day}`}>
                  {p.title}
                </option>
              ))}
            </select>
          </label>

          <button className="btn-secondary" onClick={() => setNaming(true)}>
            New plan
          </button>
        </div>
      )}

      {naming && (
        <NamePlanDialog
          copyFrom={plan ? { tier, day, title: plan.title } : null}
          busy={createPlan.isPending}
          error={createPlan.isError}
          onCancel={() => setNaming(false)}
          onCreate={async (title, start) => {
            const made = await createPlan.mutateAsync({
              title,
              copyFrom: start === 'copy' ? { tier, day } : undefined,
              fromSchedule: start === 'schedule',
            });
            setPicked({ tier: made.tier, day: made.day });
            setNaming(false);
          }}
        />
      )}

      <div className="mb-4 flex gap-1" role="group" aria-label="View">
        {VIEWS.map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            aria-pressed={view === v}
            className={[
              'rounded-lg px-2.5 py-1 text-xs transition-colors',
              view === v ? 'accent-fill' : 'bg-linen text-walnut hover:bg-sand/60',
            ].join(' ')}
          >
            {VIEW_LABELS[v]}
          </button>
        ))}
      </div>

      {view === 'plan' &&
        (plan ? (
          <PlanView
            key={`${tier}-${day}`}
            plan={plan}
            worksheets={worksheets}
            schedule={schedule}
            onPrintSheet={(id) => setPrinting(id)}
            onBuildPage={() => setView('worksheets')}
          />
        ) : (
          <NoPlansYet onNew={() => setNaming(true)} />
        ))}
      {view === 'worksheets' && (
        <WorksheetsView worksheets={worksheets} onPrint={(id) => setPrinting(id)} />
      )}
      {view === 'attachments' && <AttachmentsView attachments={data?.attachments ?? []} />}

      {printing && (
        <Portal>
          <div className="hidden print:block">
            {printing === 'packet' && plan ? (
              <Packet plan={plan} sheets={packetSheets} />
            ) : (
              (() => {
                const sheet = worksheets.find((w) => w.id === printing);
                return sheet ? <WorksheetSheet sheet={sheet} /> : null;
              })()
            )}
          </div>
        </Portal>
      )}
    </div>
  );
}

/** A day that has no plan behind it, and the way to build one. */
/**
 * Nothing written yet, which is now the honest starting point.
 *
 * The page used to open onto nine plans somebody else had written. It opens
 * empty instead, because that is the truth of it — and an empty page with one
 * obvious button beats nine plans she has to read before deleting.
 */
function NoPlansYet({ onNew }: { onNew: () => void }) {
  return (
    <section className="card max-w-lg">
      <h2 className="mb-1 text-base">No sub plans yet</h2>
      <p className="mb-4 text-sm leading-relaxed text-walnut">
        A sub plan is the day written out for somebody who has never met your class. Start one
        from your own schedule — every block already in place, with nothing written in them — and
        fill it in as you go.
      </p>

      <button className="btn-primary" onClick={onNew}>
        Write your first one
      </button>

      <p className="mt-3 text-xs leading-relaxed text-stone">
        The worksheets are still there under <strong>Worksheets</strong>, ready to attach to any
        block.
      </p>
    </section>
  );
}

/** Where a new plan begins. */
type PlanStart = 'schedule' | 'blank' | 'copy';

/** One of the three ways to start, as a button rather than a bare radio. */
function StartOption({
  value,
  picked,
  onPick,
  title,
  body,
}: {
  value: PlanStart;
  picked: PlanStart;
  onPick: (v: PlanStart) => void;
  title: string;
  body: string;
}) {
  const on = picked === value;

  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={() => onPick(value)}
      className={[
        'block w-full rounded-lg border-2 p-2.5 text-left transition-colors',
        on ? 'border-terracotta bg-terracotta/5' : 'border-sand hover:border-clay',
      ].join(' ')}
    >
      <span className="block text-sm text-espresso">{title}</span>
      <span className="mt-0.5 block text-xs leading-snug text-stone">{body}</span>
    </button>
  );
}

/**
 * Naming a plan of her own.
 *
 * Offers to start from the plan she is looking at, because that is how most of
 * these really begin — "the middle-of-year one, but for the day of the
 * assembly" — and writing a day from nothing is an afternoon's work she has
 * already done once.
 */
function NamePlanDialog({
  copyFrom,
  busy,
  error,
  onCancel,
  onCreate,
}: {
  copyFrom: { tier: SubPlanTier; day: number; title: string } | null;
  busy: boolean;
  error: boolean;
  onCancel: () => void;
  onCreate: (title: string, start: PlanStart) => void;
}) {
  const [title, setTitle] = useState('');
  const [start, setStart] = useState<PlanStart>('schedule');

  return (
    <Modal
      onClose={onCancel}
      label="New sub plan"
      className="w-full max-w-md rounded-card bg-white p-5 shadow-lifted"
    >
      <h2 className="mb-1 text-lg">A sub plan of your own</h2>
      <p className="mb-4 text-sm leading-relaxed text-walnut">
        Name it for the day you would reach for it — &ldquo;Flu week&rdquo;, &ldquo;Half
        day&rdquo;, &ldquo;The day of the assembly&rdquo;.
      </p>

      <label className="block">
        <span className="label">Name</span>
        <input
          className="field"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && title.trim()) onCreate(title.trim(), start);
          }}
        />
      </label>

      {/*
        Where it starts from.

        "Your schedule" is the one thing the shipped plans were genuinely
        useful for and the only part worth keeping: the shape of a day, already
        pinned to her timetable, with every block empty. Somebody else's
        activities were the part she did not want.
      */}
      <fieldset className="mt-4">
        <legend className="label">Start it with</legend>

        <div className="space-y-1.5">
          <StartOption
            value="schedule"
            picked={start}
            onPick={setStart}
            title="Your schedule"
            body="A block for every row of your timetable, in order, with nothing written in them."
          />
          <StartOption
            value="blank"
            picked={start}
            onPick={setStart}
            title="A blank page"
            body="Nothing at all. You add the blocks you want."
          />
          {copyFrom && (
            <StartOption
              value="copy"
              picked={start}
              onPick={setStart}
              title={`A copy of ${copyFrom.title}`}
              body="Every block, worksheet and document copied across, then yours to change."
            />
          )}
        </div>
      </fieldset>

      {error && <p className="mt-2 text-xs text-rust">That did not save. Try again in a moment.</p>}

      <div className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="btn-primary"
          disabled={busy || !title.trim()}
          onClick={() => onCreate(title.trim(), start)}
        >
          {busy ? 'Creating…' : 'Create it'}
        </button>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------ the plan */

function PlanView({
  plan,
  worksheets,
  schedule,
  onPrintSheet,
  onBuildPage,
}: {
  plan: ResolvedSubPlan;
  worksheets: Worksheet[];
  /** The live lesson-plan rows, so a block can be moved onto one by hand. */
  schedule: PlanRow[];
  onPrintSheet: (id: string) => void;
  onBuildPage: () => void;
}) {
  const update = useUpdateSubPlan(plan.tier, plan.day);
  const confirm = useConfirm();
  const [editing, setEditing] = useState<string | null>(null);

  const total = plan.blocks.reduce((sum, b) => sum + (b.minutes ?? 0), 0);

  function saveBlock(next: SubBlock) {
    const blocks = plan.blocks.map((b) =>
      b.id === next.id ? next : (stripResolved(b) as SubBlock),
    );
    update.mutate({ blocks });
    setEditing(null);
  }

  function removeBlock(id: string) {
    update.mutate({ blocks: plan.blocks.filter((b) => b.id !== id).map(stripResolved) });
  }

  function addBlock(rowId: string | null, label: string) {
    const blocks = [
      ...plan.blocks.map(stripResolved),
      {
        id: `b-${Date.now().toString(36)}`,
        rowId,
        label,
        summary: '',
        steps: [],
        materials: [],
        standards: [],
        worksheetIds: [],
        ifTimeRemains: '',
        sortOrder: plan.blocks.length,
      },
    ];
    update.mutate({ blocks });
  }

  return (
    /*
     * One sheet, not a stack of cards.
     *
     * The page used to be a card for arrival, a card per block, a card for
     * dismissal — which reads as a pile of unrelated notes. A sub plan is a
     * document somebody follows from the top of the day to the bottom, so it
     * is drawn as one: hairlines between sections instead of gaps, and the
     * times in a gutter down the left where an eye can run along them.
     */
    <div className="space-y-4">
      {plan.uncovered.length > 0 && (
        <section className="rounded-card border border-ochre bg-ochre/10 p-4">
          <h2 className="mb-1 text-base">Nothing written for these yet</h2>
          <p className="mb-3 text-sm leading-relaxed text-walnut">
            These are on your schedule but not in this sub plan. A gap in a sub plan is twenty
            minutes of chaos — worth filling before you need it.
          </p>
          <ul className="flex flex-wrap gap-2">
            {plan.uncovered.map((row) => (
              <li key={row.rowId}>
                <button onClick={() => addBlock(row.rowId, row.label)} className="btn-soft">
                  + {row.label}
                  {row.timeStart ? ` (${row.timeStart})` : ''}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <article className="overflow-hidden rounded-card border border-sand bg-white">
        <header className="border-b border-sand px-5 py-4">
          <h2 className="text-lg">{plan.title}</h2>
          <p className="mt-0.5 text-xs text-stone">
            {plan.blocks.filter((b) => b.onSchedule).length} on the schedule
            {total > 0 ? ` · ${Math.floor(total / 60)}h ${total % 60}m accounted for` : ''}
          </p>
        </header>

        <div className="border-b border-sand px-5 py-4">
          <NotesCard
            plain
            title="Before they come in"
            value={plan.arrival}
            onSave={(arrival) => update.mutate({ arrival })}
          />
        </div>

        <ol className="divide-y divide-sand">
          {plan.blocks.map((block) => (
            <li key={block.id} className={block.noStudents ? 'bg-linen/40' : ''}>
              {editing === block.id ? (
                <div className="px-5 py-4">
                  <BlockEditor
                    block={block}
                    worksheets={worksheets}
                    schedule={schedule}
                    onSave={saveBlock}
                    onCancel={() => setEditing(null)}
                    onDelete={async () => {
                      const ok = await confirm({
                        title: `Remove ${block.label}?`,
                        body: 'The block goes; your schedule is untouched.',
                        confirmLabel: 'Remove',
                        danger: true,
                      });
                      if (ok) removeBlock(block.id);
                    }}
                  />
                </div>
              ) : (
                <BlockRow
                  block={block}
                  worksheets={worksheets}
                  onEdit={() => setEditing(block.id)}
                  onPrintSheet={onPrintSheet}
                />
              )}
            </li>
          ))}
        </ol>

        <div className="border-t border-sand px-5 py-4">
          <NotesCard
            plain
            title="Home time"
            value={plan.dismissal}
            onSave={(dismissal) => update.mutate({ dismissal })}
          />
        </div>

        <div className="border-t border-sand px-5 py-4">
          <NotesCard
            plain
            title="Things that are not on the schedule"
            value={plan.notes}
            onSave={(notes) => update.mutate({ notes })}
          />
        </div>

        <div className="border-t border-sand px-5 py-4">
          <PlanDocuments
            plan={plan}
            onChange={(documentIds) => update.mutate({ documentIds })}
            onBuildPage={onBuildPage}
          />
        </div>
      </article>
    </div>
  );
}

/** A resolved block back down to what the server will accept. */
function stripResolved(block: ResolvedSubBlock | SubBlock): SubBlock {
  const {
    id,
    rowId,
    label,
    summary,
    steps,
    materials,
    standards,
    worksheetIds,
    ifTimeRemains,
    sortOrder,
    rowPinned,
  } = block;
  return {
    id,
    rowId,
    label,
    summary,
    steps,
    materials,
    standards,
    worksheetIds,
    ifTimeRemains,
    sortOrder,
    rowPinned,
  };
}

/**
 * One block, as a line of the day rather than a card of its own.
 *
 * The time sits in a gutter down the left so an eye can run along the column
 * and find eleven o'clock; the writing sits beside it. Cards put a border
 * round each of those and turned a day into thirteen unrelated notes.
 */
function BlockRow({
  block,
  worksheets,
  onEdit,
  onPrintSheet,
}: {
  block: ResolvedSubBlock;
  worksheets: Worksheet[];
  onEdit: () => void;
  onPrintSheet: (id: string) => void;
}) {
  const sheets = worksheets.filter((w) => block.worksheetIds.includes(w.id));

  return (
    <div className="group flex gap-4 px-5 py-4">
      {/* The gutter. Fixed width so every row's writing starts on one line. */}
      <div className="w-24 shrink-0 pt-0.5">
        {block.onSchedule ? (
          <>
            <p className="text-sm text-espresso">{blockTimeLabel(block.timeStart, block.timeEnd)}</p>
            {block.minutes !== null && (
              <p className="text-[11px] text-stone">{block.minutes} min</p>
            )}
          </>
        ) : (
          <p className="text-[11px] leading-snug text-rust">Not on your schedule</p>
        )}
        {block.rematched && (
          <p className="mt-0.5 text-[11px] leading-snug text-ochre" title="Matched by name after your schedule changed">
            matched by name
          </p>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-base">
            {block.label}
            {block.noStudents && (
              <span className="ml-2 text-xs font-normal text-stone">students are elsewhere</span>
            )}
          </h3>

          {/* Hidden until wanted: an Edit button on every row is clutter on a
              page that is mostly read, not written. */}
          <button
            onClick={onEdit}
            className="btn-soft shrink-0 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
          >
            Edit
          </button>
        </div>

        {block.summary && (
          <p className="mt-1 text-sm leading-relaxed text-walnut">{block.summary}</p>
        )}

        {block.steps.length > 0 && (
          <ol className="mt-2 space-y-1.5">
            {block.steps.map((step, i) => (
              <li key={i} className="flex gap-2 text-sm leading-relaxed">
                <span className="mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-linen text-[10px] text-walnut">
                  {i + 1}
                </span>
                <span className="min-w-0">{step}</span>
              </li>
            ))}
          </ol>
        )}

        {(block.materials.length > 0 || block.standards.length > 0) && (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone">
            {block.materials.length > 0 && <span>Needs: {block.materials.join(', ')}</span>}
            {block.standards.length > 0 && <span>Standards: {block.standards.join(', ')}</span>}
          </div>
        )}

        {block.ifTimeRemains && (
          <p className="mt-2 rounded-lg bg-linen/70 px-3 py-2 text-xs leading-relaxed text-walnut">
            If there is time left: {block.ifTimeRemains}
          </p>
        )}

        {sheets.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs text-stone">Pages to print:</span>
            {sheets.map((sheet) => (
              <button
                key={sheet.id}
                onClick={() => onPrintSheet(sheet.id)}
                className="btn-soft-accent"
              >
                {sheet.title}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The documents a substitute is handed.
 *
 * They live in Documents rather than in a store of their own, which is the
 * whole design decision here: the permission slip uploaded on this page is the
 * same file that turns up on the Documents page, is searched by the same
 * search, and prints through the same code. The alternative — a private pile
 * of files that only a sub plan can see — means uploading the same thing
 * twice and finding it in neither place.
 */
function PlanDocuments({
  plan,
  onChange,
  onBuildPage,
}: {
  plan: ResolvedSubPlan;
  onChange: (documentIds: string[]) => void;
  /** Takes her to the worksheet builder, which is where a page is made. */
  onBuildPage: () => void;
}) {
  const upload = useUploadDocument();
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [printing, setPrinting] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const attached = plan.documents;

  function attach(id: string) {
    if (plan.documentIds.includes(id)) return;
    onChange([...plan.documentIds, id]);
  }

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;

    setBusy(true);
    const added: string[] = [];
    try {
      // One at a time, as on the Documents page: several large uploads at once
      // is several simultaneous re-encodes on a small container.
      for (const file of Array.from(files)) {
        try {
          const made = await upload.mutateAsync({ file });
          added.push(made.id);
        } catch {
          // Keep going — one refused file should not stop the others.
        }
      }
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }

    if (added.length > 0) onChange([...plan.documentIds, ...added]);
  }

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base">Documents to hand them</h2>

        <div className="flex flex-wrap gap-1.5">
          <input
            ref={fileInput}
            type="file"
            accept={DOCUMENT_RULES.acceptedMimeTypes.join(',')}
            multiple
            className="hidden"
            onChange={(e) => void onFiles(e.target.files)}
          />
          <button
            className="btn-soft"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            {busy ? 'Adding…' : 'Upload a file'}
          </button>
          <button className="btn-soft" onClick={() => setPicking(true)}>
            From Documents
          </button>
          <button className="btn-soft" onClick={onBuildPage}>
            Build a page
          </button>

          {/*
            The same printing the Documents page uses, so a PDF prints as a
            PDF and each sheet comes out the way round it was written.
          */}
          {attached.length > 0 && (
            <button
              className="btn-soft-accent"
              disabled={printing}
              onClick={async () => {
                setPrinting(true);
                try {
                  await printDocuments(
                    attached
                      .filter((d) => canPrintInBrowser(d.contentType))
                      .map((d) => ({
                        url: documentUrlById(d.id),
                        contentType: d.contentType,
                      })),
                  );
                } finally {
                  setPrinting(false);
                }
              }}
            >
              {printing ? 'Preparing…' : 'Print these'}
            </button>
          )}
        </div>
      </div>

      <p className="mb-3 text-xs leading-relaxed text-stone">
        Anything added here is also in your Documents, so it is searchable and printable from there
        too. <strong>Build a page</strong> opens the worksheet builder — the same tool the
        built-in handouts are made with.
      </p>

      {attached.length === 0 ? (
        <p className="text-sm text-stone">Nothing attached yet.</p>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {attached.map((doc) => (
            <li
              key={doc.id}
              className="flex items-center gap-3 rounded-lg border border-sand bg-linen/30 p-2"
            >
              <div className="h-14 w-11 shrink-0 overflow-hidden rounded border border-sand bg-white">
                <DocumentThumbnail
                  document={{
                    id: doc.id,
                    name: doc.name,
                    fileName: doc.fileName,
                    contentType: doc.contentType,
                    size: doc.size,
                    note: '',
                    pinned: false,
                    uploadedAt: '',
                  }}
                />
              </div>

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-espresso">{doc.name}</p>
                <p className="text-[11px] text-stone">
                  {documentKind(doc.contentType)} · {fileSizeLabel(doc.size)}
                </p>
              </div>

              <button
                className="btn-soft shrink-0"
                aria-label={`Take ${doc.name} off this plan`}
                title="Take it off this plan — the document itself stays in Documents"
                onClick={() => onChange(plan.documentIds.filter((id) => id !== doc.id))}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      {picking && (
        <PickDocument
          already={plan.documentIds}
          onPick={(id) => {
            attach(id);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </section>
  );
}

/** Choosing something already filed, rather than uploading a second copy. */
function PickDocument({
  already,
  onPick,
  onClose,
}: {
  already: string[];
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useDocuments(search);

  const documents = data?.documents ?? [];

  return (
    <Modal
      onClose={onClose}
      label="Choose a document"
      className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-card bg-white p-5 shadow-lifted"
    >
      <h2 className="mb-3 text-lg">From your documents</h2>

      <input
        className="field mb-3"
        placeholder="Find anything, including inside…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Search your documents"
        autoFocus
      />

      {isLoading ? (
        <p className="text-sm text-stone">Loading…</p>
      ) : documents.length === 0 ? (
        <p className="text-sm text-stone">
          {search ? `Nothing matched "${search}".` : 'You have no documents yet.'}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {documents.map((doc) => {
            const on = already.includes(doc.id);
            return (
              <li key={doc.id}>
                <button
                  className="flex w-full items-center gap-3 rounded-lg border border-sand p-2 text-left transition-colors hover:border-clay disabled:opacity-40"
                  disabled={on}
                  onClick={() => onPick(doc.id)}
                >
                  <div className="h-12 w-9 shrink-0 overflow-hidden rounded border border-sand bg-white">
                    <DocumentThumbnail document={doc} />
                  </div>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-espresso">{doc.name}</span>
                    <span className="block text-[11px] text-stone">
                      {documentKind(doc.contentType)} · {fileSizeLabel(doc.size)}
                      {on ? ' · already on this plan' : ''}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

function BlockEditor({
  block,
  worksheets,
  schedule,
  onSave,
  onCancel,
  onDelete,
}: {
  block: ResolvedSubBlock;
  worksheets: Worksheet[];
  schedule: PlanRow[];
  onSave: (b: SubBlock) => void;
  onCancel: () => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState(() => ({
    ...stripResolved(block),
    stepsText: block.steps.join('\n'),
    materialsText: block.materials.join(', '),
    standardsText: block.standards.join(', '),
  }));

  return (
    <section className="rounded-card border border-terracotta bg-white p-4">
      <label className="block">
        <span className="label">Name</span>
        <input
          className="field"
          value={draft.label}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
        />
      </label>

      {/*
        Which row this sits on, and the one place to disagree with the guess.

        A block whose row has gone is re-matched by name, which is right nearly
        always. This is for the times it is not — two rows with similar names,
        or a block she wants left off the schedule on purpose. Choosing here
        pins it, and the matching leaves it alone from then on.
      */}
      <label className="mt-3 block">
        <span className="label">When</span>
        <select
          className="field"
          value={draft.rowId ?? ''}
          onChange={(e) =>
            setDraft({ ...draft, rowId: e.target.value || null, rowPinned: true })
          }
        >
          <option value="">Not on the schedule</option>
          {schedule.map((row) => (
            <option key={row.id} value={row.id}>
              {row.label}
              {row.timeStart ? ` · ${row.timeStart}${row.timeEnd ? `–${row.timeEnd}` : ''}` : ''}
            </option>
          ))}
        </select>
      </label>

      <p className="mt-1 text-[11px] leading-relaxed text-stone">
        The time comes from your lesson plan schedule and is not typed here — change it in Lesson
        Plans and this block moves with it.
        {block.rematched && !draft.rowPinned && (
          <>
            {' '}
            <span className="text-ochre">
              This one was matched back to your schedule by name after the schedule was rebuilt.
              Pick a row above if it landed in the wrong place.
            </span>
          </>
        )}
      </p>

      <label className="mt-3 block">
        <span className="label">In one sentence</span>
        <input
          className="field"
          value={draft.summary}
          onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
          placeholder="What this block is, for someone who has never seen it."
        />
      </label>

      <label className="mt-3 block">
        <span className="label">What to do — one step per line</span>
        <textarea
          className="field min-h-[10rem]"
          value={draft.stepsText}
          onChange={(e) => setDraft({ ...draft, stepsText: e.target.value })}
        />
      </label>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="label">Materials, comma separated</span>
          <input
            className="field"
            value={draft.materialsText}
            onChange={(e) => setDraft({ ...draft, materialsText: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="label">Standards, comma separated</span>
          <input
            className="field"
            value={draft.standardsText}
            onChange={(e) => setDraft({ ...draft, standardsText: e.target.value })}
            placeholder="1.RF.5, 1.RC.1"
          />
        </label>
      </div>

      <label className="mt-3 block">
        <span className="label">If there is time left</span>
        <input
          className="field"
          value={draft.ifTimeRemains}
          onChange={(e) => setDraft({ ...draft, ifTimeRemains: e.target.value })}
        />
      </label>

      <fieldset className="mt-3">
        <legend className="label">Pages the children need</legend>
        <div className="flex flex-wrap gap-1.5">
          {worksheets.map((w) => {
            const on = draft.worksheetIds.includes(w.id);
            return (
              <button
                key={w.id}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  setDraft({
                    ...draft,
                    worksheetIds: on
                      ? draft.worksheetIds.filter((id) => id !== w.id)
                      : [...draft.worksheetIds, w.id],
                  })
                }
                className={[
                  'rounded-full px-2.5 py-1 text-[11px] transition-colors',
                  on ? 'accent-fill' : 'bg-linen text-walnut hover:bg-sand/60',
                ].join(' ')}
              >
                {w.title}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          className="btn-primary"
          onClick={() =>
            onSave({
              ...stripResolved(draft as unknown as SubBlock),
              steps: draft.stepsText
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean),
              materials: splitList(draft.materialsText),
              standards: splitList(draft.standardsText),
            })
          }
        >
          Save
        </button>
        <button className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn-danger-solid ml-auto" onClick={onDelete}>
          Remove block
        </button>
      </div>
    </section>
  );
}

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * One written section of the plan.
 *
 * `plain` drops the card chrome. The page used to be a stack of separate
 * cards — arrival, then a card per block, then dismissal — which read as a
 * pile of unrelated notes rather than as a day. Inside the sheet these are
 * sections of one document, so the border and the white panel would be drawing
 * a box around a paragraph.
 */
function NotesCard({
  title,
  value,
  plain = false,
  onSave,
}: {
  title: string;
  value: string;
  plain?: boolean;
  onSave: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  // The saved copy wins whenever it changes underneath an untouched form.
  useEffect(() => setDraft(value), [value]);

  return (
    <section className={plain ? '' : 'rounded-card border border-sand bg-white p-4'}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <h2 className="text-base">{title}</h2>
        <button
          className="btn-soft shrink-0"
          onClick={() => {
            if (editing) onSave(draft);
            setEditing(!editing);
          }}
        >
          {editing ? 'Save' : 'Edit'}
        </button>
      </div>

      {editing ? (
        <textarea
          className="field min-h-[10rem]"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
      ) : value.trim() ? (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-walnut">{value}</p>
      ) : (
        <p className="text-sm text-stone">Nothing written here yet.</p>
      )}
    </section>
  );
}

/* ------------------------------------------------------- the worksheets */

function WorksheetsView({
  worksheets,
  onPrint,
}: {
  worksheets: Worksheet[];
  onPrint: (id: string) => void;
}) {
  const remove = useDeleteWorksheet();
  const update = useUpdateWorksheet();
  const create = useCreateWorksheet();
  const confirm = useConfirm();
  const [preview, setPreview] = useState<string | null>(worksheets[0]?.id ?? null);
  /** The sheet in the editor, and whether it is one she is making from scratch. */
  const [editing, setEditing] = useState<{ sheet: Worksheet; isNew: boolean } | null>(null);

  const showing = worksheets.find((w) => w.id === preview) ?? null;
  const busy = editing?.isNew ? create : update;

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[18rem_1fr]">
      {/*
        The list scrolls inside itself rather than making the page forty
        worksheets long. Otherwise choosing one near the bottom leaves the
        page scrolled past the sheet you just chose, looking at nothing.
      */}
      <div className="space-y-1.5 lg:sticky lg:top-4 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto lg:pr-1">
        <button
          className="btn-secondary w-full"
          onClick={() => setEditing({ sheet: blankWorksheet(), isNew: true })}
        >
          + New worksheet
        </button>

        <ul className="space-y-1.5">
          {worksheets.map((w) => (
          <li key={w.id}>
            <button
              onClick={() => setPreview(w.id)}
              aria-pressed={w.id === preview}
              className={[
                'w-full rounded-card border p-3 text-left transition-colors',
                w.id === preview
                  ? 'border-terracotta bg-linen/60'
                  : 'border-sand bg-white hover:border-clay',
              ].join(' ')}
            >
              <span className="block text-sm text-espresso">{w.title}</span>
              <span className="block text-[11px] text-stone">
                {w.subject}
                {w.tier ? ` · ${SUB_TIER_LABELS[w.tier]}` : ' · any time'}
              </span>
            </button>
            </li>
          ))}
        </ul>
      </div>

      {/*
        And the sheet stays put as the list moves. It is the thing being
        looked at; it should not be the thing that scrolls away.
      */}
      <div className="lg:sticky lg:top-4">
        {showing ? (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <button onClick={() => onPrint(showing.id)} className="btn-primary">
                Print this page
              </button>
              <button
                onClick={() => setEditing({ sheet: showing, isNew: false })}
                className="btn-secondary"
              >
                Edit
              </button>
              {!showing.builtIn && (
                <button
                  className="btn-soft-danger ml-auto"
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Delete ${showing.title}?`,
                      body: 'Any block pointing at it stops pointing at it.',
                      confirmLabel: 'Delete',
                      danger: true,
                    });
                    if (ok) remove.mutate(showing.id);
                  }}
                >
                  Delete
                </button>
              )}
            </div>

            <div className="max-h-[calc(100vh-10rem)] overflow-auto rounded-card border border-sand">
              <WorksheetSheet sheet={showing} />
            </div>
          </>
        ) : (
          <p className="text-sm text-stone">No worksheets yet.</p>
        )}
      </div>

      {editing && (
        <WorksheetEditor
          worksheet={editing.sheet}
          isNew={editing.isNew}
          saving={busy.isPending}
          error={saveError(busy.error)}
          onClose={() => {
            busy.reset();
            setEditing(null);
          }}
          onSave={(next) => {
            const input = {
              title: next.title,
              tier: next.tier,
              subject: next.subject,
              instructions: next.instructions,
              standards: next.standards,
              parts: next.parts,
            };

            if (editing.isNew) {
              create.mutate(input, {
                // Straight to the new sheet, so she sees what she just built.
                onSuccess: (made) => {
                  setPreview(made.id);
                  setEditing(null);
                },
              });
            } else {
              update.mutate(
                { id: next.id, ...input },
                { onSuccess: () => setEditing(null) },
              );
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * Why a save did not take, in as much detail as the server gave.
 *
 * "That input is not valid" is what she saw the first time this went wrong,
 * and it tells nobody anything. If the server named the field, say so — it is
 * the difference between a bug she can report and a button that is haunted.
 */
function saveError(err: unknown): string | null {
  if (!err) return null;

  if (err instanceof ApiRequestError) {
    const issues = (err.details as { issues?: { path: string; message: string }[] } | undefined)
      ?.issues;

    if (issues?.length) {
      return `${err.message} — ${issues.map((i) => `${i.path}: ${i.message}`).join('; ')}`;
    }
    return err.message;
  }

  return err instanceof Error ? err.message : 'Something went wrong';
}

/**
 * A worksheet she has not written yet.
 *
 * Not empty: a prompt and lines to answer on is what a first-grade page nearly
 * always starts as, and starting from something is easier than starting from a
 * blank screen. Everything here can be taken straight back off.
 */
function blankWorksheet(): Worksheet {
  return {
    id: '',
    title: '',
    tier: null,
    subject: '',
    instructions: '',
    standards: [],
    parts: [
      { kind: 'text', text: 'Write the question here.' },
      { kind: 'lines', lines: 3 },
    ],
    builtIn: false,
    sortOrder: 0,
  };
}

/* ------------------------------------------------------- the attachments */

function AttachmentsView({ attachments }: { attachments: SubAttachment[] }) {
  const uploadFile = useUploadAttachment();
  const addLink = useAddLink();
  const remove = useDeleteAttachment();
  const confirm = useConfirm();
  const fileInput = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const accept = useMemo(() => SUB_FILE_RULES.acceptedMimeTypes.join(','), []);

  async function onFile(file: File) {
    setError(null);
    try {
      await uploadFile.mutateAsync({ file, name: name.trim() || file.name, note });
      setName('');
      setNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That file could not be attached');
    }
  }

  return (
    <div className="space-y-4">
      <section className="rounded-card border border-sand bg-white p-4">
        <h2 className="mb-1 text-base">Anything the sub might need</h2>
        <p className="mb-3 text-sm leading-relaxed text-walnut">
          Fire drill procedures, the dismissal list, a photo of the room, a link to the district
          page. PDFs, Word files and images, up to 10&nbsp;MB each.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label">What is it?</span>
            <input
              className="field"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Fire drill procedure"
            />
          </label>
          <label className="block">
            <span className="label">Why the sub needs it</span>
            <input
              className="field"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Read this first thing"
            />
          </label>
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-2">
          <input
            ref={fileInput}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFile(file);
              e.target.value = '';
            }}
          />
          <button
            className="btn-primary"
            onClick={() => fileInput.current?.click()}
            disabled={uploadFile.isPending}
          >
            {uploadFile.isPending ? 'Attaching…' : 'Attach a file'}
          </button>

          <span className="text-xs text-stone">or</span>

          <label className="min-w-[14rem] flex-1">
            <span className="label">A link</span>
            <input
              className="field"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
            />
          </label>
          <button
            className="btn-secondary"
            disabled={!url.trim() || !name.trim() || addLink.isPending}
            onClick={async () => {
              setError(null);
              try {
                await addLink.mutateAsync({ name: name.trim(), note, url: url.trim() });
                setName('');
                setNote('');
                setUrl('');
              } catch (err) {
                setError(err instanceof Error ? err.message : 'That link could not be saved');
              }
            }}
          >
            Add the link
          </button>
        </div>

        {error && <p className="mt-2 text-xs text-rust">{error}</p>}
      </section>

      {attachments.length === 0 ? (
        <div className="relative rounded-card border border-dashed border-sand p-6">
          <p className="text-sm text-stone">
            Nothing attached yet. Whatever the office hands you at the start of the year probably
            belongs here.
          </p>
          <span className="pointer-events-none absolute bottom-2 right-3 text-clay opacity-[0.12]">
            <Doodle name="paperclip" size={56} />
          </span>
        </div>
      ) : (
        <ul className="space-y-2">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex flex-wrap items-center gap-3 rounded-card border border-sand bg-white p-3"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-espresso">{a.name}</span>
                <span className="block truncate text-[11px] text-stone">
                  {a.note || (a.kind === 'link' ? a.url : a.fileName)}
                  {a.kind === 'file' && a.size ? ` · ${Math.round(a.size / 1024)} KB` : ''}
                </span>
              </span>

              {a.kind === 'link' ? (
                <a
                  href={a.url ?? '#'}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn-soft-accent"
                >
                  Open
                </a>
              ) : (
                <a
                  href={`/api/sub-plans/attachments/${a.id}/file`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn-soft-accent"
                >
                  {isImageAttachment(a.contentType) ? 'View' : 'Download'}
                </a>
              )}

              <button
                className="btn-soft-danger"
                onClick={async () => {
                  const ok = await confirm({
                    title: `Remove ${a.name}?`,
                    body: 'It is gone from the sub packet.',
                    confirmLabel: 'Remove',
                    danger: true,
                  });
                  if (ok) remove.mutate(a.id);
                }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ printing */

/** The whole thing on paper: the day, then every page the children need. */
function Packet({ plan, sheets }: { plan: ResolvedSubPlan; sheets: Worksheet[] }) {
  return (
    <div className="print-sheet text-espresso">
      <section className="break-after-page p-8 print:p-0">
        <h1 className="font-display text-3xl">Substitute plans — {plan.title}</h1>
        <p className="mt-1 text-sm">
          Everything you need is in here. You should not need to find anything on a computer.
        </p>

        {plan.arrival && (
          <>
            <h2 className="mt-5 border-b-2 border-espresso pb-1 font-display text-xl">
              Before they come in
            </h2>
            <p className="mt-2 whitespace-pre-wrap text-[15px] leading-relaxed">{plan.arrival}</p>
          </>
        )}

        <h2 className="mt-6 border-b-2 border-espresso pb-1 font-display text-xl">The day</h2>

        <ol className="mt-3 space-y-4">
          {plan.blocks.map((block) => (
            <li key={block.id} className="break-inside-avoid">
              <h3 className="text-[17px] font-semibold">
                {block.onSchedule ? `${blockTimeLabel(block.timeStart, block.timeEnd)} · ` : ''}
                {block.label}
              </h3>
              {block.summary && <p className="text-[15px] leading-snug">{block.summary}</p>}

              {block.steps.length > 0 && (
                <ol className="ml-5 mt-1 list-decimal space-y-0.5 text-[15px] leading-snug">
                  {block.steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
              )}

              {block.materials.length > 0 && (
                <p className="mt-1 text-[13px]">You will need: {block.materials.join(', ')}</p>
              )}
              {block.ifTimeRemains && (
                <p className="mt-1 text-[13px] italic">
                  If there is time left: {block.ifTimeRemains}
                </p>
              )}
            </li>
          ))}
        </ol>

        {plan.dismissal && (
          <>
            <h2 className="mt-6 border-b-2 border-espresso pb-1 font-display text-xl">Home time</h2>
            <p className="mt-2 whitespace-pre-wrap text-[15px] leading-relaxed">{plan.dismissal}</p>
          </>
        )}

        {plan.notes && (
          <>
            <h2 className="mt-6 border-b-2 border-espresso pb-1 font-display text-xl">
              Things that are not on the schedule
            </h2>
            <p className="mt-2 whitespace-pre-wrap text-[15px] leading-relaxed">{plan.notes}</p>
          </>
        )}
      </section>

      {/*
        Each page of children's work starts its own sheet. The break lives on
        the worksheet itself now rather than on a wrapper here — a wrapper
        with a page break before it and a margin on the element inside was
        producing a blank sheet between every page.
      */}
      {sheets.map((sheet) => (
        <WorksheetSheet key={sheet.id} sheet={sheet} />
      ))}
    </div>
  );
}
